/**
 * features/legal/LegalPage.tsx
 * ------------------------------------------------------------------
 * The public legal pages — /privacy, /terms, /data-deletion — rendered
 * outside the app, with no sign-in, the same way the enquiry form is.
 *
 * Deliberately a plain reading page: one column, generous line length,
 * print-friendly, and the same whether or not the visitor's browser prefers
 * dark mode, because this is a document people read and sometimes print.
 * The words live in legalContent.ts.
 * ---------------------------------------------------------------- */
import { COMPANY } from '@/features/landing/work';
import { LEGAL_DOCS, type LegalSlug } from './legalContent';

const LINKS: { slug: LegalSlug; label: string }[] = [
  { slug: 'privacy', label: 'Privacy' },
  { slug: 'terms', label: 'Terms' },
  { slug: 'data-deletion', label: 'Data deletion' },
];

/** Which legal page, if any, this address is. Stable paths are what Meta's
 *  app review and anyone we send a link to will use; `?privacy` also works. */
export function legalSlugFor(loc: Location): LegalSlug | null {
  const path = loc.pathname.replace(/\/+$/, '').toLowerCase();
  const q = new URLSearchParams(loc.search);
  for (const { slug } of LINKS) {
    if (path === `/${slug}` || q.has(slug)) return slug;
  }
  return null;
}

/** Turn the plain "/privacy" and "/data-deletion" mentions in the copy into links. */
function withLinks(text: string) {
  const parts = text.split(/(\/privacy|\/terms|\/data-deletion)/g);
  return parts.map((part, i) =>
    /^\/(privacy|terms|data-deletion)$/.test(part)
      ? <a key={i} href={part} className="text-[#c2410c] underline">{part.replace('/', '')} page</a>
      : part,
  );
}

export function LegalPage({ slug }: { slug: LegalSlug }) {
  const doc = LEGAL_DOCS[slug];
  if (typeof document !== 'undefined') document.title = `${doc.title} — ${COMPANY.legalName}`;

  return (
    <div className="min-h-screen bg-[#faf9f7] text-[#1f2328]">
      <header className="border-b border-[#e7e3dd] bg-white">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <a href="/" className="text-lg font-semibold tracking-tight text-[#1f2328] no-underline">
            {COMPANY.displayName}
          </a>
          <nav className="flex gap-4 text-sm">
            {LINKS.map((l) => (
              <a
                key={l.slug}
                href={`/${l.slug}`}
                aria-current={l.slug === slug ? 'page' : undefined}
                className={l.slug === slug ? 'font-semibold text-[#c2410c]' : 'text-[#57606a] hover:text-[#c2410c]'}
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-3xl font-bold tracking-tight text-[#1f2328]">{doc.title}</h1>
        <p className="mt-1 text-sm text-[#57606a]">
          {COMPANY.legalName} &middot; Last updated {doc.updated}
        </p>
        <p className="mt-6 text-base leading-7 text-[#1f2328]">{doc.intro}</p>

        {doc.sections.map((sec) => (
          <section key={sec.heading} className="mt-8">
            <h2 className="text-lg font-semibold text-[#1f2328]">{sec.heading}</h2>
            {sec.body.map((block, i) =>
              Array.isArray(block) ? (
                <ul key={i} className="mt-3 list-disc space-y-2 pl-6 text-base leading-7 text-[#1f2328]">
                  {block.map((item, j) => <li key={j}>{withLinks(item)}</li>)}
                </ul>
              ) : (
                <p key={i} className="mt-3 text-base leading-7 text-[#1f2328]">{withLinks(block)}</p>
              ),
            )}
          </section>
        ))}
      </main>

      <footer className="border-t border-[#e7e3dd] bg-white">
        <div className="mx-auto max-w-3xl px-5 py-6 text-sm leading-6 text-[#57606a]">
          <p>
            {COMPANY.legalName} (trading as {COMPANY.displayName}) &middot; Company reg. {COMPANY.registration}
          </p>
          <p>{COMPANY.address}</p>
          <p>
            <a href={`mailto:${COMPANY.email}`} className="text-[#c2410c]">{COMPANY.email}</a>
            {' '}&middot; {COMPANY.phone}
          </p>
        </div>
      </footer>
    </div>
  );
}
